'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { useAuth } from '@/lib/auth'
import { Mail, Lock, User, Phone, UserPlus, Leaf, Apple, Carrot, Croissant, Milk, Salad, Cherry, Wheat, X, Check, Shield, FileText, Eye, EyeOff } from 'lucide-react'

// Password strength checker
function getPasswordStrength(password: string): { score: number; label: string; color: string } {
  let score = 0
  if (password.length >= 8) score++
  if (/[a-z]/.test(password)) score++
  if (/[A-Z]/.test(password)) score++
  if (/[0-9]/.test(password)) score++
  if (/[^a-zA-Z0-9]/.test(password)) score++

  if (score <= 2) return { score, label: 'Slabé', color: 'bg-red-500' }
  if (score <= 3) return { score, label: 'Průměrné', color: 'bg-yellow-500' }
  if (score <= 4) return { score, label: 'Dobré', color: 'bg-blue-500' }
  return { score, label: 'Silné', color: 'bg-green-500' }
}

// Terms Modal Component
function TermsModal({ isOpen, onClose, type }: { isOpen: boolean; onClose: () => void; type: 'terms' | 'privacy' }) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-primary-50 rounded-t-xl">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            {type === 'terms' ? (
              <>
                <FileText className="w-5 h-5 text-primary-600" />
                Podmínky použití
              </>
            ) : (
              <>
                <Shield className="w-5 h-5 text-primary-600" />
                Zásady ochrany osobních údajů (GDPR)
              </>
            )}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {type === 'terms' ? <TermsContent /> : <PrivacyContent />}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex items-center justify-between">
            <Link
              href={type === 'terms' ? '/terms' : '/privacy'}
              target="_blank"
              className="text-sm text-primary-600 hover:underline"
            >
              Otevřít v novém okně
            </Link>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Zavřít
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Terms Content
function TermsContent() {
  return (
    <div className="prose prose-sm max-w-none">
      <h3>1. Úvodní ustanovení</h3>
      <p>
        Tyto Podmínky použití upravují pravidla pro používání platformy Stop Plýtvání Potravin,
        která slouží ke sdílení a záchraně potravin před zbytečným plýtváním.
      </p>
      <p>
        Provozovatelem je Stop Plýtvání Potravin s.r.o. Registrací na platformě uživatel vyjadřuje
        souhlas s těmito podmínkami.
      </p>

      <h3>2. Registrace a uživatelský účet</h3>
      <ul>
        <li>Pro používání platformy je nutná registrace. Uživatel musí být starší 18 let.</li>
        <li>Uživatel je povinen uvést pravdivé a aktuální údaje.</li>
        <li>Každý uživatel smí mít pouze jeden účet.</li>
        <li>Heslo musí obsahovat minimálně 8 znaků, velké a malé písmeno a číslo.</li>
      </ul>

      <h3>3. Pravidla používání</h3>
      <p>Uživatel se zavazuje:</p>
      <ul>
        <li>Nabízet pouze potraviny vhodné ke konzumaci</li>
        <li>Uvádět pravdivé informace o nabízených potravinách</li>
        <li>Dodržovat hygienické standardy</li>
        <li>Komunikovat s ostatními uživateli slušně</li>
      </ul>

      <h3>4. Zakázaný obsah</h3>
      <p>Na platformě je zakázáno nabízet:</p>
      <ul>
        <li>Potraviny po datu spotřeby</li>
        <li>Alkohol a tabákové výrobky</li>
        <li>Léky a potravinové doplňky</li>
        <li>Potraviny neznámého původu</li>
      </ul>

      <h3>5. Odpovědnost</h3>
      <p>
        Provozovatel poskytuje platformu jako zprostředkovatele mezi uživateli.
        Uživatelé nesou plnou odpovědnost za potraviny, které nabízejí i přijímají.
      </p>

      <h3>6. Porušení podmínek</h3>
      <p>
        V případě porušení podmínek může provozovatel vydat varování,
        omezit funkce účtu nebo účet trvale zablokovat.
      </p>

      <h3>7. Závěrečná ustanovení</h3>
      <p>
        Tyto podmínky se řídí právním řádem České republiky.
        Platné od 1. ledna 2025, verze 1.0.
      </p>

      <div className="bg-gray-100 p-4 rounded-lg mt-4">
        <p className="text-sm text-gray-600 m-0">
          Kompletní znění podmínek najdete na stránce{' '}
          <Link href="/terms" target="_blank" className="text-primary-600 hover:underline">
            Podmínky použití
          </Link>
        </p>
      </div>
    </div>
  )
}

// Privacy Content
function PrivacyContent() {
  return (
    <div className="prose prose-sm max-w-none">
      <h3>1. Správce údajů</h3>
      <p>
        Stop Plýtvání Potravin s.r.o., IČO: 12345678, Praha, Česká republika.
        Kontakt: gdpr@stopplytvani.cz
      </p>

      <h3>2. Jaké údaje shromažďujeme</h3>
      <p><strong>Údaje, které nám poskytujete:</strong></p>
      <ul>
        <li>Registrační údaje (jméno, e-mail, telefon)</li>
        <li>Profilové údaje a fotografie</li>
        <li>Údaje o nabídkách a komunikaci</li>
      </ul>
      <p><strong>Automaticky shromažďované údaje:</strong></p>
      <ul>
        <li>Technické údaje (IP adresa, typ prohlížeče)</li>
        <li>Údaje o používání aplikace</li>
      </ul>

      <h3>3. Účely zpracování</h3>
      <ul>
        <li>Poskytování služby a správa účtu</li>
        <li>Zprostředkování komunikace mezi uživateli</li>
        <li>Zlepšování služby</li>
        <li>Prevence podvodů a bezpečnost</li>
      </ul>

      <h3>4. Doba uchovávání</h3>
      <ul>
        <li>Údaje o účtu: po dobu existence účtu + 30 dní</li>
        <li>Transakční údaje: 3 roky</li>
        <li>Komunikace: 1 rok</li>
      </ul>

      <h3>5. Vaše práva podle GDPR</h3>
      <ul>
        <li><strong>Právo na přístup</strong> - kopie všech vašich údajů</li>
        <li><strong>Právo na opravu</strong> - oprava nepřesných údajů</li>
        <li><strong>Právo na výmaz</strong> - smazání údajů</li>
        <li><strong>Právo na přenositelnost</strong> - export ve strojově čitelném formátu</li>
        <li><strong>Právo odvolat souhlas</strong> - kdykoli</li>
      </ul>

      <h3>6. Zabezpečení</h3>
      <p>
        Používáme HTTPS šifrování, BCrypt hashování hesel, přístupovou kontrolu
        a průběžný monitoring bezpečnostních hrozeb.
      </p>

      <h3>7. Kontakt</h3>
      <p>
        Pro dotazy ohledně ochrany osobních údajů: gdpr@stopplytvani.cz
      </p>
      <p>
        Stížnosti můžete podat u Úřadu pro ochranu osobních údajů (www.uoou.cz).
      </p>

      <div className="bg-gray-100 p-4 rounded-lg mt-4">
        <p className="text-sm text-gray-600 m-0">
          Kompletní znění najdete na stránce{' '}
          <Link href="/privacy" target="_blank" className="text-primary-600 hover:underline">
            Zásady ochrany osobních údajů
          </Link>
        </p>
      </div>
    </div>
  )
}

export default function RegisterPage() {
  const router = useRouter()
  const { register, isLoading, error, clearError } = useAuth()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  })
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [validationError, setValidationError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [modalType, setModalType] = useState<'terms' | 'privacy' | null>(null)

  const passwordStrength = getPasswordStrength(formData.password)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) return 'Heslo musí mít alespoň 8 znaků'
    if (!/[a-z]/.test(password)) return 'Heslo musí obsahovat malé písmeno'
    if (!/[A-Z]/.test(password)) return 'Heslo musí obsahovat velké písmeno'
    if (!/[0-9]/.test(password)) return 'Heslo musí obsahovat číslo'
    return null
  }

  const isFormValid = () => {
    return (
      formData.name.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.password.length >= 8 &&
      /[a-z]/.test(formData.password) &&
      /[A-Z]/.test(formData.password) &&
      /[0-9]/.test(formData.password) &&
      formData.password === formData.confirmPassword &&
      termsAccepted
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    setValidationError('')

    if (!termsAccepted) {
      setValidationError('Pro registraci musíte souhlasit s podmínkami použití a ochranou osobních údajů')
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setValidationError('Hesla se neshodují')
      return
    }

    const passwordError = validatePassword(formData.password)
    if (passwordError) {
      setValidationError(passwordError)
      return
    }

    try {
      await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        password: formData.password,
        termsAccepted: termsAccepted,
      })
      router.push('/dashboard')
    } catch (error) {
      // Error is handled in the store
    }
  }

  return (
    <>
      <div className="min-h-screen flex">
        {/* Left side - Green branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-primary-700 relative overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute top-16 left-8 w-48 h-48 bg-white/5 rounded-full"></div>
          <div className="absolute top-32 right-12 w-32 h-32 bg-white/5 rounded-full"></div>
          <div className="absolute bottom-24 left-16 w-64 h-64 bg-white/5 rounded-full"></div>
          <div className="absolute bottom-48 right-8 w-24 h-24 bg-white/5 rounded-full"></div>

          {/* Floating food icons - flat white style */}
          <Apple className="absolute top-20 left-24 w-8 h-8 text-white/20" />
          <Carrot className="absolute top-40 right-28 w-10 h-10 text-white/15" />
          <Croissant className="absolute bottom-40 left-20 w-9 h-9 text-white/20" />
          <Milk className="absolute bottom-20 right-20 w-8 h-8 text-white/15" />
          <Salad className="absolute top-1/2 left-16 w-10 h-10 text-white/15" />
          <Cherry className="absolute top-28 right-20 w-7 h-7 text-white/20" />
          <Wheat className="absolute bottom-32 right-32 w-9 h-9 text-white/15" />
          <Leaf className="absolute top-2/3 right-16 w-8 h-8 text-white/20" />

          {/* Center content */}
          <div className="flex flex-col items-center justify-center w-full z-10 px-12">
            <img src="/logo.png" alt="Stop Plýtvání Potravin" className="h-32 w-auto mb-6" />
            <p className="text-white/80 text-center text-base max-w-xs">
              Přidejte se k nám a pomozte snížit plýtvání jídlem ve vaší komunitě.
            </p>
          </div>
        </div>

        {/* Right side - Registration form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-50 px-6 py-12">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="lg:hidden text-center mb-6">
              <div className="w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-3 p-2">
                <img src="/logo.png" alt="Stop Plýtvání Potravin" className="h-full w-auto" />
              </div>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Registrace</h2>
              <p className="text-gray-500 mt-2">Vytvořte si nový účet</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {(error || validationError) && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {error || validationError}
                </div>
              )}

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Jméno
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="Jan Novák"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="vas@email.cz"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Telefon <span className="text-gray-400">(volitelné)</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="+420 123 456 789"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Heslo
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {/* Password strength indicator */}
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${passwordStrength.color} transition-all duration-300`}
                          style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                        />
                      </div>
                      <span className={`text-xs font-medium ${
                        passwordStrength.score <= 2 ? 'text-red-500' :
                        passwordStrength.score <= 3 ? 'text-yellow-500' :
                        passwordStrength.score <= 4 ? 'text-blue-500' : 'text-green-500'
                      }`}>
                        {passwordStrength.label}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                      <span className={formData.password.length >= 8 ? 'text-green-600' : ''}>
                        {formData.password.length >= 8 ? <Check className="w-3 h-3 inline mr-0.5" /> : '•'} 8+ znaků
                      </span>
                      <span className={/[a-z]/.test(formData.password) ? 'text-green-600' : ''}>
                        {/[a-z]/.test(formData.password) ? <Check className="w-3 h-3 inline mr-0.5" /> : '•'} malé
                      </span>
                      <span className={/[A-Z]/.test(formData.password) ? 'text-green-600' : ''}>
                        {/[A-Z]/.test(formData.password) ? <Check className="w-3 h-3 inline mr-0.5" /> : '•'} velké
                      </span>
                      <span className={/[0-9]/.test(formData.password) ? 'text-green-600' : ''}>
                        {/[0-9]/.test(formData.password) ? <Check className="w-3 h-3 inline mr-0.5" /> : '•'} číslo
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Potvrzení hesla
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                      formData.confirmPassword && formData.password !== formData.confirmPassword
                        ? 'border-red-300 bg-red-50'
                        : formData.confirmPassword && formData.password === formData.confirmPassword
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-300'
                    }`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">Hesla se neshodují</p>
                )}
              </div>

              {/* Terms and Privacy checkbox */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <div className="flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
                    />
                  </div>
                  <span className="text-sm text-gray-600">
                    Souhlasím s{' '}
                    <button
                      type="button"
                      onClick={() => setModalType('terms')}
                      className="text-primary-600 hover:underline font-medium"
                    >
                      Podmínkami použití
                    </button>
                    {' '}a{' '}
                    <button
                      type="button"
                      onClick={() => setModalType('privacy')}
                      className="text-primary-600 hover:underline font-medium"
                    >
                      Zásadami ochrany osobních údajů (GDPR)
                    </button>
                    .
                  </span>
                </label>
                {!termsAccepted && (
                  <p className="text-xs text-gray-500 mt-2 ml-8">
                    Pro registraci je nutné souhlasit s podmínkami.
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || !isFormValid()}
                className={`w-full font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  isFormValid()
                    ? 'bg-primary-600 hover:bg-primary-700 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <UserPlus className="w-5 h-5" />
                {isLoading ? 'Registrace...' : 'Zaregistrovat se'}
              </button>
            </form>

            <div className="mt-8 text-center">
              <span className="text-gray-500">Již máte účet? </span>
              <Link href="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                Přihlaste se
              </Link>
            </div>

            <p className="mt-8 text-center text-xs text-gray-400">
              © 2025 Stop Plýtvání Potravin. Všechna práva vyhrazena.
            </p>
          </div>
        </div>
      </div>

      {/* Terms/Privacy Modal */}
      <TermsModal
        isOpen={modalType !== null}
        onClose={() => setModalType(null)}
        type={modalType || 'terms'}
      />
    </>
  )
}
